import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { DialogFooter } from "@/components/ui/dialog";
import { ApiError } from "../api-errors.ts";
import { t as translate } from "../i18n/i18n.ts";
import { changeSetPath, navigate } from "../router.tsx";
import { useRevert } from "./queries.ts";

/** What a confirmation restores: one version of a page, or everything a change set published. */
export type RestoreTarget =
  | { kind: "version"; pageId: string; version: number; title: string }
  | { kind: "changeSet"; id: string; title: string; pages: number };

/** A refused restore or revert, explained without naming what made it refused. */
export function restoreRefusal(error: Error, kind: RestoreTarget["kind"]): string {
  if (!(error instanceof ApiError)) {
    return error.message;
  }
  if (error.status === 404) {
    return translate(
      kind === "version" ? "wiki.restore.refused.version" : "wiki.restore.refused.changeSet",
    );
  }
  if (error.status === 403) {
    return translate("wiki.restore.refused.forbidden");
  }
  if (error.status === 409 && error.code === "version_current") {
    return translate("wiki.restore.refused.current");
  }
  if (error.status === 409 && error.code === "project_frozen") {
    return translate("wiki.restore.refused.frozen");
  }
  if (error.status === 409 && kind === "changeSet" && error.code === "conflict") {
    return translate("wiki.restore.refused.notPublished");
  }
  // Any other refusal (too many open change sets, a stale item) is already localized by code.
  return error.message;
}

/**
 * Confirms a restore or a revert. Either one creates a change set for review and changes no
 * page; on success its review opens. A refusal keeps the dialog open and says why.
 */
export function RestoreDialog(props: {
  projectId: string;
  target: RestoreTarget | null;
  onClose: () => void;
  /** Where focus returns when the opening control is gone (a menu item). */
  returnFocus?: () => HTMLElement | null;
}) {
  const { t } = useTranslation();
  const revert = useRevert(props.projectId);
  const target = props.target;
  const close = () => {
    if (!revert.isPending) {
      revert.reset();
      props.onClose();
    }
  };
  return (
    <AppDialog
      open={target !== null}
      onClose={close}
      returnFocus={props.returnFocus}
      title={
        target?.kind === "version"
          ? t("wiki.restore.pageTitle", { version: target.version, title: target.title })
          : t("wiki.restore.setTitle", { title: target?.title ?? "" })
      }
      description={
        target?.kind === "version"
          ? t("wiki.restore.pageBody", { version: target.version })
          : t("wiki.restore.setBody", { count: target?.pages ?? 0 })
      }
    >
      <div className="flex flex-col gap-4">
        {revert.isError && target ? (
          <Notice tone="danger">{restoreRefusal(revert.error, target.kind)}</Notice>
        ) : null}
        <DialogFooter>
          <Button variant="outline" disabled={revert.isPending} onClick={close}>
            {t("wiki.restore.cancel")}
          </Button>
          <Button
            disabled={revert.isPending || !target}
            onClick={() => {
              if (!target) {
                return;
              }
              revert.mutate(
                target.kind === "version"
                  ? { pageId: target.pageId, version: target.version }
                  : { changeSetId: target.id },
                {
                  onSuccess: (created) => {
                    props.onClose();
                    navigate(changeSetPath(props.projectId, created.id));
                  },
                },
              );
            }}
          >
            {revert.isPending ? t("wiki.restore.working") : t("wiki.restore.confirm")}
          </Button>
        </DialogFooter>
      </div>
    </AppDialog>
  );
}
